// ODOO-4443 (этап 2): общий вычислитель правил условного оформления
// (conditional_format). Вынесен из AttrGrid.vue, чтобы правила Игоря красили не
// только ячейки гридов, но и точки/серии чартов (первый потребитель —
// AttrBubbleChart). Семантика — паритет с бэком (data_layout2.check_WHERE_row)
// и с прежними методами грида: AttrGrid делегирует сюда, поведение ячеек не
// меняется.
//
// ctx: { getValue(obj, binding) } — доступ к значению строки (store.get_value).
// IE 2026 10 07 / ODOO-5800: ctx.getValueInherit(obj, binding) — необязательный: значение поля с наследованием от родительских строк DL2 (поле строки отсутствует).

// IE 2026 09 18 / ODOO-5800 (находка ie-dm7: подсветка правил-метрик расходилась с метриками бэка): «заполнено» — как на бэке
// (python bool(value) в data_layout2._check_WHERE_one, SQL: число <> 0, булево IS TRUE): 0, false, '', пустой список — НЕ заполнено.
// Раньше set считал заполненным всё, кроме null и '' — «!set» не ловил нули, «set» ловил. Прежняя трактовка — cfSetLegacy(true)
// (настройка базы fe.data.cfSetLegacy, выставляет грид).
let CF_SET_LEGACY = false
export function cfSetLegacy(on) { CF_SET_LEGACY = !!on }
function cfIsSet(v) {
    if (CF_SET_LEGACY) return v != null && v !== ''
    if (v == null) return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object') return Object.keys(v).length > 0
    return !!v
}
// SQL LIKE в памяти — как бэк (_like_match): шаблон без % — вхождение, % — любая подстрока, _ — один символ
function cfLike(value, pattern) {
    const v = value == null ? '' : String(value)
    let p = pattern == null ? '' : String(pattern)
    if (!p.includes('%') && !p.includes('_')) return v.toLowerCase().includes(p.toLowerCase())   // прежнее поведение фронта: без учёта регистра
    if (!p.includes('%')) p = '%' + p + '%'
    const rx = new RegExp('^' + [...p].map(ch => ch === '%' ? '[\\s\\S]*' : (ch === '_' ? '[\\s\\S]' : ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('') + '$', 'i')
    return rx.test(v)
}

// IE 2026 09 20 / ODOO-5800 (Igor: «для периодов с..по — красным, если периодС > периодПо»): сравнение «поле с полем» — правая часть '#field.<binding>' =
// значение другого поля той же строки (паритет с бэком: data_layout2.WHERE_FIELD_REF). Поле-ссылка пусто — условие не выполнено, кроме отрицаний при заданном левом.
export const CF_FIELD_REF = '#field.'
export function cfCheckCondition(left, w, refValue) {
    const rv = w.rightValue
    let right = (rv && typeof rv === 'object') ? rv.value : rv
    if (typeof right === 'string' && right.startsWith(CF_FIELD_REF) && !['in', '!in', 'set', '!set'].includes(w.condition)) {
        right = typeof refValue === 'function' ? refValue(right.slice(CF_FIELD_REF.length)) : undefined
        if (right == null || right === '') return ['!=', '!like'].includes(w.condition) && left != null && left !== ''
    }
    const isNum = !!(rv && typeof rv === 'object' && rv.attr_type === 'AttrNumber')
    switch (w.condition) {
        case '=':
            if (left == null) return right == null || right === ''
            return isNum ? +left === +right : left == right
        case '!=':
            if (left == null) return !(right == null || right === '')
            return isNum ? +left !== +right : left != right
        case '<':
            return left != null && (isNum ? +left < +right : left < right)
        case '>':
            return left != null && (isNum ? +left > +right : left > right)
        // паритет с бэком (check_WHERE_row): нестрогие сравнения падали в
        // default:false, пороговые правила (>=90) не красили
        case '<=':
            return left != null && (isNum ? +left <= +right : left <= right)
        case '>=':
            return left != null && (isNum ? +left >= +right : left >= right)
        case 'like':
            return left != null && right != null && cfLike(left, right)
        case '!like':   // IE 2026 09 18 / ODOO-5800: отрицания раньше падали в default:false — правило гасло
            return !(left != null && right != null && cfLike(left, right))
        case 'in':
        case '!in': {
            const hit = (w.attrs || []).some(a => {
                if (a && a.use === false) return false
                const v = (a?.rightValue && typeof a.rightValue === 'object') ? a.rightValue.value : a?.rightValue
                return isNum ? +v === +left : v == left
            })
            return w.condition === 'in' ? hit : !hit
        }
        case 'set':
            return cfIsSet(left)
        case '!set':
            return !cfIsSet(left)
        default:
            return false
    }
}

export function cfResolveRef(ref, col_binding, attr_col) {
    // #value — значение самой ячейки; #cell.<raw> — поле-спутник той же
    // колонки-даты (см. комментарии в AttrGrid до выноса)
    if (!col_binding) return null
    if (ref === '#value') return col_binding
    if (ref.startsWith('#cell.')) {
        const raw = ref.slice(6)
        const raw0 = attr_col?.raw_binding
        if (raw0) {
            const i = col_binding.lastIndexOf(raw0)
            if (i >= 0) {
                return col_binding.slice(0, i) + raw + col_binding.slice(i + raw0.length)
            }
            return col_binding
        }
        return raw
    }
    return null
}

// IE 2026 09 18 / ODOO-5800 (находка ie-dm7): группы #and / #or в WHERE правила — та же грамматика, что на бэке
// (data_layout2._where_row_is_group / _check_WHERE_group): leftValue '#and' / '#or' либо condition 'and' / 'or', подчинённые
// строки — в attrs; пустая группа (нет включённых строк) в условии не участвует. Раньше группа читалась как обычная строка
// с неизвестным условием → default:false гасил всё правило
function cfLeftOf(w) { return (w.leftValue && typeof w.leftValue === 'object') ? w.leftValue.value : w.leftValue }
export function cfWhereIsGroup(w) {
    const lv = cfLeftOf(w)
    return lv === '#and' || lv === '#or' || w.condition === 'and' || w.condition === 'or'
}
// включённые листовые строки WHERE сквозь группы
export function cfWhereLeaves(rows, out = []) {
    for (const w of rows || []) {
        if (!w || w.use === false) continue
        if (cfWhereIsGroup(w)) cfWhereLeaves(w.attrs, out); else out.push(w)
    }
    return out
}
function cfCheckOne(w, dataItem, col_binding, attr_col, ctx) {
    let lv = cfLeftOf(w)
    // IE 2026 10 07 / ODOO-5800 (Igor: «если grade не выводить в строке 4, теряется условное оформление, хотя grade есть у родительской строки»):
    // поле-условие (не #value / #cell.* — значение самой ячейки) отсутствует в строке — берём у ближайшего предка (ctx.getValueInherit; только для оценки правила)
    let getV = ctx.getValue
    if (typeof lv === 'string' && lv.startsWith('#')) {
        lv = cfResolveRef(lv, col_binding, attr_col)
        if (lv == null) return false
    } else if (ctx.getValueInherit) {
        getV = ctx.getValueInherit
    }
    return cfCheckCondition(getV(dataItem, lv), w, (b) => getV(dataItem, b))
}
function cfCheckGroup(g, dataItem, col_binding, attr_col, ctx) {
    const isOr = cfLeftOf(g) === '#or' || g.condition === 'or'
    const results = []
    for (const w of g.attrs || []) {
        if (!w || w.use === false) continue
        const r = cfWhereIsGroup(w) ? cfCheckGroup(w, dataItem, col_binding, attr_col, ctx) : cfCheckOne(w, dataItem, col_binding, attr_col, ctx)
        if (r !== null) results.push(r)
    }
    if (!results.length) return null
    return isOr ? results.some(Boolean) : results.every(Boolean)
}

export function cfCheckWHERE(task, dataItem, col_binding, attr_col, ctx) {
    for (const w of task.WHERE || []) {
        if (!w || w.use === false) continue
        if (cfWhereIsGroup(w)) {
            if (cfCheckGroup(w, dataItem, col_binding, attr_col, ctx) === false) return false
            continue
        }
        if (!cfCheckOne(w, dataItem, col_binding, attr_col, ctx)) return false
    }
    return true
}

// IE 2026 10 14 / ODOO-5800 (Igor: замирание при клике по флажку согласования под правилами оформления): поля строки, которые читают условия
// правил (все каналы: report / sets[*] / fields[*]; группы #and / #or — насквозь): leftValue без «#» и правая часть «#field.<поле>». Ссылки «#value» /
// «#cell.<raw>» — значение самой ячейки, строки других не касаются. По этому набору грид решает, может ли правка колонки изменить оформление
// ДРУГИХ строк (наследование значения предка, fe.ux.cfInheritParent) — editKeep_scope в AttrGrid.vue
export function cfRulesFields(cf_rules) {
    const out = new Set()
    if (!cf_rules || typeof cf_rules !== 'object') return out
    const takeTask = (task) => {
        for (const w of cfWhereLeaves(task?.WHERE)) {
            const lv = cfLeftOf(w)
            if (typeof lv === 'string' && lv && !lv.startsWith('#')) out.add(lv)
            const rv = (w.rightValue && typeof w.rightValue === 'object') ? w.rightValue.value : w.rightValue
            if (typeof rv === 'string' && rv.startsWith(CF_FIELD_REF) && rv.length > CF_FIELD_REF.length) out.add(rv.slice(CF_FIELD_REF.length))
        }
    }
    const takeList = (list) => { if (Array.isArray(list)) for (const t of list) takeTask(t) }
    if (Array.isArray(cf_rules)) { takeList(cf_rules); return out }
    takeList(cf_rules.report)
    for (const k in cf_rules.sets || {}) takeList(cf_rules.sets[k])
    for (const k in cf_rules.fields || {}) takeList(cf_rules.fields[k])
    return out
}

// ---- «дельта» класса для превью вне настоящей ячейки грида ----
// Классы, объявленные только в контексте ячейки (.wj-cell.cell-rc,
// .wj-flexgrid .foo и т.п.), на превью-элементе (мини-ячейка Preview настроек,
// чип FORMATS в dropdown) не срабатывают. Считаем отличие проб внутри host
// (два скрытых .wj-cell: с классом и без) и отдаём CSS-текст отличающихся
// свойств — его вешают инлайном на превью. Кэш по имени класса; записи
// CSS-CLASS инвалидируют его при (пере)регистрации стилей.
const _classDeltaCache = new Map()
const CF_DELTA_PROPS = ['background-color', 'background-image', 'color',
    'font-weight', 'font-style', 'text-decoration-line', 'border-radius', 'opacity']

export function cfClassDeltaCss(cls, host) {
    if (!cls) return ''
    if (_classDeltaCache.has(cls)) return _classDeltaCache.get(cls)
    let css = ''
    try {
        const root = host || document.body
        const mk = (withCls) => {
            const el = document.createElement('div')
            el.className = withCls ? 'wj-cell ' + cls : 'wj-cell'
            el.style.cssText = 'position:absolute;left:-9999px;visibility:hidden'
            root.appendChild(el)
            return el
        }
        const a = mk(false), b = mk(true)
        const ca = getComputedStyle(a), cb = getComputedStyle(b)
        for (const prop of CF_DELTA_PROPS) {
            const v = cb.getPropertyValue(prop)
            if (v !== ca.getPropertyValue(prop)) css += `${prop}:${v};`
        }
        a.remove(); b.remove()
    } catch (e) {
        css = ''
    }
    _classDeltaCache.set(cls, css)
    return css
}

export function cfClassDeltaInvalidate(cls) {
    if (cls) _classDeltaCache.delete(cls)
    else _classDeltaCache.clear()
}

// ---- цвет задания для «не-ячейковых» потребителей (точки чартов) ----
// Ячейку красят cssClasses/cssStyle; точке нужен ОДИН цвет. Берём его из
// cssStyle (background/background-color/fill), иначе резолвим первый класс
// через computed style одноразового элемента (кэшируется по имени класса).
const _classColorCache = new Map()

// Ячейковые классы — намеренно бледные подложки под текст (#ffdede и т.п.);
// точке/серии чарта нужен насыщенный аналог того же семантического цвета.
const CF_CLASS_CHART_COLORS = {
    red: '#e53935', orange: '#fb8c00', yellow: '#f2b600',
    green: '#43a047', grey: '#90a4ae',
    blue: '#1e88e5', 'blue-strong': '#1565c0',   // IE 2026 10 01 / ODOO-5800 (Igor 01.10): синяя шкала сверхнормативного запаса
}

// IE 2026 09 18 / ODOO-…: семантический тон именованных стилей сегмента CSS-CLASS (тот же, что у их прежних двойников
// cell-background-*); стиль без тона (stock-ok, полосы, горизонты) графику цвета не задаёт
export const CF_NAMED_STYLE_TONE = {
    'stock-critical': 'red', 'grade-bad': 'red', 'alert-uncovered': 'red', 'alert-row': 'red', 'kpi-bad': 'red', 'value-negative': 'red',
    'stock-min': 'orange', 'grade-warning': 'orange', 'alert-expired': 'orange',
    'stock-opt': 'yellow', 'kpi-mid': 'yellow', 'grade-notice': 'yellow',
    'stock-max': 'green', 'grade-good': 'green', 'kpi-good': 'green',
    'stock-nosales': 'grey', 'status-muted': 'grey',
    'stock-over-max': 'blue', 'stock-excess': 'blue-strong',   // IE 2026 10 01 / ODOO-5800 (Igor 01.10): запас > max / > 2×max — синяя шкала
}

export function cfTaskColor(task, opts) {
    const style = String(task?.cssStyle || '')
    const m = style.match(/(?:background-color|background|fill)\s*:\s*([^;]+)/i)
    if (m) return m[1].trim()
    if (opts?.vivid) {
        for (const cls of task?.cssClasses || []) {
            const mc = String(cls).match(/^cell-background-([\w-]+)$/)   // IE 2026 10 01 / ODOO-5800: + cell-background-blue-strong
            if (mc && CF_CLASS_CHART_COLORS[mc[1]]) return CF_CLASS_CHART_COLORS[mc[1]]
        }
        // IE 2026 09 18 / ODOO-… (именованные стили): правило может нести только именованный стиль (после второго прохода
        // привязки прежний класс cell-background-* снимается) — графику нужен тот же насыщенный семантический цвет
        for (const cls of task?.cssClasses || []) {
            const tone = CF_NAMED_STYLE_TONE[String(cls)]
            if (tone && CF_CLASS_CHART_COLORS[tone]) return CF_CLASS_CHART_COLORS[tone]
        }
    }
    for (const cls of task?.cssClasses || []) {
        if (_classColorCache.has(cls)) {
            const c = _classColorCache.get(cls)
            if (c) return c
            continue
        }
        let color = null
        try {
            const el = document.createElement('div')
            el.className = cls
            el.style.position = 'absolute'
            el.style.left = '-9999px'
            document.body.appendChild(el)
            const bg = getComputedStyle(el).backgroundColor
            document.body.removeChild(el)
            if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') color = bg
        } catch (e) { /* среда без DOM — цвет только из cssStyle */ }
        _classColorCache.set(cls, color)
        if (color) return color
    }
    return null
}

// Задания поля для потребителя-чарта: kind measure/cell по биндингу меры,
// отсортированные как в гриде (order по убыванию — последний применённый
// побеждает; для одного цвета берём ПЕРВОЕ совпавшее с конца списка).
export function cfFieldTasks(cf_rules, binding) {
    if (!cf_rules || !binding) return []
    const tasks = []
    for (const task of cf_rules.report || []) {
        if ((task.kind === 'cell' || task.kind === 'measure') && task.binding === binding) tasks.push(task)
    }
    for (const task of (cf_rules.fields && cf_rules.fields[binding]) || []) {
        if (task.kind !== 'row') tasks.push(task)
    }
    if (tasks.length > 1) tasks.sort((a, b) => (+b.order || 0) - (+a.order || 0))
    return tasks
}
